import { Modal, useMantineTheme } from "@mantine/core";
import React, { useEffect, useState, useRef } from "react";
import DatumView from "../Feed/Datum/DatumView";
import ItemView from "../Feed/Item/ItemView";
import UserView from "../../User/UserView";
import { useDispatch, useSelector } from "react-redux";
import { closeModalAction } from "../../../actions/ModalAction";
import { getDatum_One } from "../../../api/DatumManipulation";
import { getUser } from "../../../api/UserRequest";
import { getItemDetail } from "../../../api/ItemRequest";
import LoadingSpinner from "../Feed/Item/LoadingSpinner";

const GeneralModal = () => {
  const { modalData, rModalOpened, loading } = useSelector((state) => state.modalReducer);
  const { user } = useSelector((state) => state.authReducer.authData);
  const dispatch = useDispatch();
  const theme = useMantineTheme();
  const hasLoggedModal = useRef(null);

  // Local state for resolved datum (used when modalType is referenceDatum)
  const [datum, setDatum] = useState();
  const [datumLoading, setDatumLoading] = useState(false)
  const [stamped, setStamped] = useState();
  const [stamps, setStamps] = useState();
  const [viewed, setViewed] = useState();
  const [views, setViews] = useState();

  useEffect(() => {
    console.log("modalData._id changed to:", modalData?._id);
    console.log("stamped before reset:", stamped);
    console.log("stamps before reset:", stamps);
}, [modalData?._id]);

  // Fetch datum whenever a referenceDatum modal is triggered
  useEffect(() => {
    if (!modalData || modalData.modalType !== "referenceDatum" || !modalData._id) return;

    setDatum(null)
    setStamped(null)
    setStamps(null)
    setViewed(null)
    setViews(null)

    const fetchDatum = async () => {
      setDatumLoading(true)
      const res = await getDatum_One(modalData._id);
      const logDetails = { 
        activity: "load_user",
        category: 'interaction',
        userId: user._id,
        sessionId: localStorage.getItem('sessionId'),
        data: {
          userId: user._id,
          username: user.username
        }
      };
      const encodedLogDetails = encodeURIComponent(JSON.stringify(logDetails));

      const authorDetails = await getUser(res.data.userId, encodedLogDetails);
      res.data.authorId = authorDetails.data._id;
      res.data.authorName = authorDetails.data.username;
      res.data.authorImage = authorDetails.data.userImage;

      for (let r = 0; r < res.data.references.length; r++) {
        const refId = res.data.references.shift();
        const referenceDetails = await getItemDetail(refId);
        res.data.references.push(referenceDetails.data);
      }

      const d = res.data;
      setDatum(d);
      setStamped(d.stamps.includes(user._id));
      setStamps(d.stamps.length);
      setViewed(d.views.includes(user._id));
      setViews(d.views.length);
      setDatumLoading(false)
    };

    fetchDatum();
  }, [modalData?._id, rModalOpened]);

  useEffect(() => {
    if (!rModalOpened || !modalData) return;
    const modalKey = `${modalData.modalType}_${modalData._id || modalData.data?._id}_${Date.now()}`;
    
    if (hasLoggedModal.current === modalKey) return;
    
    const stableKey = `${modalData.modalType}_${modalData._id || modalData.data?._id}`;
    if (hasLoggedModal.current === stableKey) return;
    hasLoggedModal.current = stableKey;

    const sessionId = localStorage.getItem('sessionId');
    const baseLog = {
        category: 'interaction',
        userId: user._id,
        sessionId,
        data: { userId: user._id, username: user.username }
    };

    let logDetails = null;

    switch (modalData.modalType) {
        case 'datum':
        case 'referenceDatum':
            logDetails = {
                ...baseLog,
                activity: 'datum_view_opened',
                datumId: modalData._id || modalData.data?._id || null,
            };
            break;
        case 'item':
        case 'referenceItem':
            logDetails = {
                ...baseLog,
                activity: 'item_view_opened',
                itemId: modalData._id || modalData.data?._id || null,
            };
            break;
        case 'user':
            logDetails = {
                ...baseLog,
                activity: 'user_view_opened',
                data: {
                    ...baseLog.data,
                    viewedUserId: modalData.data?._id || null,
                    viewedUsername: modalData.data?.username || null,
                }
            };
            break;
        default:
            break;
    }

    if (!logDetails) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/logs`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: JSON.stringify(logDetails) })
    });

  }, [rModalOpened, modalData?.modalType, modalData?._id]);

  const closeModal = () => {
    dispatch(closeModalAction());
    setDatum();
    setStamped(null);
    setStamps(null);
    setViewed(null);
    setViews(null);
    hasLoggedModal.current = null;
  };

  const getTitle = () => {
    if (!modalData) return "";
    if (modalData.modalType === "referenceItem") return modalData.itemName || "Item";
    if (modalData.modalType === "referenceDatum") return "Detail View";
    if (modalData.modalType === "datum") return "View Datum";
    if (modalData.modalType === "item") return modalData.data.itemName || "Item";
    if (modalData.modalType === "user") return modalData.data.username || "User";
    return "";
  };

  const renderContent = () => {
    if (!modalData) return null;
    if (loading) {
      return (<LoadingSpinner />);
    }

    switch (modalData.modalType) {
      case "datum":        
        return (
          <DatumView
            data={modalData.data}
            stampedProp={modalData.stamped}
            setStampedProp={modalData.setStamped}
            stampsProp={modalData.stamps}
            setStamps={modalData.setStamps}
            viewedProp={modalData.viewed}
            setViewedProp={modalData.setViewed}
            viewsProp={modalData.views}
            setViewsProp={modalData.setViews}
            datumtype={modalData.datumtype}
            setMainContent={modalData.setMainContent}
          />
        );

      case "referenceDatum":
        return datumLoading? (
          <LoadingSpinner />
        ): datum ? (
          <DatumView
            data={datum}
            stampedProp={stamped}
            setStampedProp={setStamped}
            stampsProp={stamps}
            setStamps={setStamps}
            viewedProp={viewed}
            setViewedProp={setViewed}
            viewsProp={views}
            setViewsProp={setViews}
            datumtype={modalData.datumtype}
            setMainContent={modalData.setMainContent}
          />
        ) : <span className="flex item-center justify-center ">Loading...</span>;

      case "referenceItem":
        return <ItemView data={modalData} />;
      case "user":
        return (
          <UserView
            user={modalData.data}
          />
        )
      case "item":
        return (
          <ItemView
            data={modalData.data}
          />
      )

      default:
        return <span>Unknown modal type</span>;
    }
  };
  const modalSize = () => {
    const width = window.innerWidth;
    if (width < 650) {
      return width * 0.8;
    } else {
      return 575;
    }
  }

  return (
    <Modal
      opened={rModalOpened}
      onClose={closeModal}
      title={getTitle()}
      overlayProps={{
        color: theme.colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[2],
        opacity: 0.55,
        blur: 3,
      }}
      size={modalSize()}
      style={{ top: 70 }}
    >
      {renderContent()}
    </Modal>
  );
};

export default GeneralModal;